import {EventEmitter, Injectable, TemplateRef} from '@angular/core';
import {ModalService} from '../modal/modal.service';
import {AlertService} from '../notify/alert.service';
import {HelpersService} from '../helpers.service';
import {Router} from '@angular/router';
import {BehaviorSubject, catchError, EMPTY, Observable, Subject} from 'rxjs';
import {
    CompanyContractModel,
    ContractMemberModel,
    ContractModel,
    ContractStepModel,
    ContractWrapperModel,
    PaymentTransactionModel,
    TPrivateContractModel,
} from '../../models/contract.model';
import {TaskModel} from '../../models/task.model';
import {ContractsService} from '../api/contracts.service';
import {DocumentsServiceService} from '../../controllers/documents/documents-service.service';
import {DocumentsApiService} from '../api/documents-api.service';
import {CrmService} from '../api/crm.service';
import {FinanceService} from '../api/finance.service';
import {CdkDrag} from '@angular/cdk/drag-drop';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {MemberModel} from '../../models/user.model';
import {MemberService} from '../member.service';
import {TranslateService} from '@ngx-translate/core';
import {TContractGraphType} from '../../modules/graph-konva/models/graph-kn.cm-api-models';
import {TPaymentData} from 'src/app/models/finance.model';

export interface IStepNameData {
    stepName: string,
    step: ContractStepModel
}

@Injectable({
    providedIn: 'root',
})
export class ContractControlService {
    public isLoadingContractList = false;
    public isLoadingContractListError = false;
    public isLoadingContract = false;

    public stepsElement:any = {};

    public subStepDataChanged: Subject<IStepNameData> = new Subject<IStepNameData>();
    public subUserPrivilegesSet: Subject<boolean> = new BehaviorSubject<boolean>(false);
    public emitOnEdit: EventEmitter<any> = new EventEmitter<any>();
    public paymentModel: PaymentTransactionModel = new PaymentTransactionModel();
    public target = '';
    public selectedStep: ContractStepModel;
    public targetItem: any;
    public typeOpenViewNodal = '';

    public refreshStepsEmit: EventEmitter<any> = new EventEmitter<any>();

    public allSignatoriesInContract: ContractMemberModel[] = [];
    public isCurrentMemberSigner = false;

    public currentMember: MemberModel;

    public emitOnDeleteStep: EventEmitter<any> = new EventEmitter<any>();

    public copyContract: ContractWrapperModel | null;

    bindSortPredicate: any;

    isCurrentMemberInContract: boolean;

    constructor(
        protected modalService: ModalService,
        protected alertService: AlertService,
        protected helper: HelpersService,
        protected router: Router,
        protected documentService: DocumentsServiceService,
        protected contractApi: ContractsService,
        protected crmApi: CrmService,
        protected documentApi: DocumentsApiService,
        protected financeApi: FinanceService,
        protected memberService: MemberService,
        public translate: TranslateService
    ) {
        this.bindSortPredicate = this.sortPredicate.bind(this);
    }

    getContracts(pagination, guid, currentProject?: string): Subject<any> {
        this.isLoadingContractList = true;
        this.isLoadingContractListError = false;
        const listContracts$: Subject<any> = new Subject();
        if (currentProject) {
            pagination.current_project_guid = currentProject;
        }
        if (guid) {
            this.contractApi.getAllMembersContract(pagination, guid).subscribe({
                next: (data) => {
                    listContracts$.next(data);
                    this.isLoadingContractList = false;
                }, error: err => {
                    this.isLoadingContractList = false;
                    this.isLoadingContractListError = true;
                    this.alertService.setAlert(err.message, 'blue');
                },
            });
        }
        return listContracts$;
    }


    getContractsForTable(pagination, guid, isAdmin): Subject<any> {
        this.isLoadingContractList = true;
        this.isLoadingContractListError = false;
        const listContracts$: Subject<any> = new Subject();
        this.contractApi.getAll(pagination, guid, isAdmin).subscribe({
            next: (data) => {
                listContracts$.next(data);
                this.isLoadingContractList = false;
            }, error: err => {
                this.isLoadingContractList = false;
                this.isLoadingContractListError = true;
                this.alertService.setAlert(err.message, 'blue');
            },
        });
        return listContracts$;
    }

    getAvailableSubcontractsForTable(pagination, contractGuid): Subject<any> {
        this.isLoadingContractList = true;
        this.isLoadingContractListError = false;
        const listContracts$: Subject<any> = new Subject();
        this.contractApi.getAvailableSubcontracts(pagination, contractGuid).subscribe({
            next: (data) => {
                listContracts$.next(data);
                this.isLoadingContractList = false;
            }, error: err => {
                this.isLoadingContractList = false;
                this.isLoadingContractListError = true;
                this.alertService.setAlert(err.message, 'blue');
            },
        });
        return listContracts$;
    }

    getContractByGuid(guid): Subject<ContractWrapperModel> {
        if (!this.currentMember) {
            this.memberService.getCurrentMember().then(res => {
                this.currentMember = res;
            });
        }
        const contract$: Subject<ContractWrapperModel> = new Subject();
        this.isLoadingContract = true;
        this.contractApi.getByGuid(guid).subscribe({
            next: async (data: any) => {
                data.contract.roadmap_active.steps = data.contract.roadmap_active.steps.map((step: ContractStepModel) => {
                    if (step.target_type === 'credit_letter') {
                        step.target.payer_project = data.contract.projects.find(
                            (project: CompanyContractModel) => project.credit_letter_guid === step.target_key
                        );
                        step.target.payer_private = data.contract.privates.find(
                            (person: TPrivateContractModel) => person.credit_letter_guid === step.target_key
                        );
                    }
                    return step;
                });
                const contract = Object.assign(new ContractWrapperModel(), data.contract);
                if (!this.copyContract) {
                    this.copyContract = structuredClone(data.contract);
                }

                if (contract.roadmap && contract.roadmap.length) {
                    for (const step of contract.roadmap) {
                        /* если нет привязанного элемента или он загружен на сервере */
                        if (!step.target || step.element) {
                            continue;
                        }
                        await this.loadContractCurrentStep(contract, step);
                    }
                }

                contract$.next(contract);
                this.isLoadingContract = false;
            }, error: err => {
                contract$.error(err);
            },
        });
        return contract$;
    }

    async loadContractCurrentStep(contract: ContractModel, step: ContractStepModel): Promise<any> {
        if (!step.target) {
            return;
        }
        step.element = null;
        switch (step.target_type) {
            case 'file':
                await this.documentApi.getFileData(step.target_key).subscribe(data => {
                    step.element = data.document;
                });
                break;
            case 'collection':
                const r = this.documentService.prepare({filter: 'collection', id: step.target_key});
                await this.documentApi.getCollectionTable(r).subscribe(res => {
                    step.element = res.folder;
                });
                break;
            case 'task':
                await this.crmApi.getTask(step.target_key).subscribe((data: { task: TaskModel }) => {
                    step.element = data.task;
                });
                break;
            case 'payment':
                await this.financeApi.getPayment(step.target_key).subscribe((data: {
                    transaction: PaymentTransactionModel
                }) => {
                    step.element = data.transaction;
                });
                break;
        }
    }

    updateContractItems(contractId, items): Subject<string> {
        const obs$: Subject<string> = new Subject();
        this.contractApi.editContract(contractId, items).subscribe({
            next: (data: { contract: ContractWrapperModel, message: string }) => {
                obs$.next(data.message);
            },
            error: errors => {
                obs$.error(errors.message);
                this.alertService.setAlert(errors.message, 'blue');
            },
        });
        return obs$;
    }

    public async deleteContract(contract: ContractModel, creator: boolean, newDesign:boolean=false): Promise<any> {
        return new Promise(resolve => {
            let message: string;
            if (creator) {
                message = this.translate.instant('CONFIRM.TEXT.DELETE_CONTRACT', {name: contract.name});
            } else {
                message = this.translate.instant('CONFIRM.TEXT.LEAVE_CONTRACT', {name: contract.name});
            }
            const modalSettings: any = {
                modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
                modalBody: message,
                buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
                buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
            };
            const modal = this.modalService.createFromSettings(modalSettings);
            modal.result.then(data => {
                if (data) {
                    this.contractApi.deleteContract(contract.guid).subscribe({
                        next: () => {
                            resolve(true);
                            void this.router.navigate(['/account/contracts/all']);
                        }, error: err => {
                            resolve(false);
                            this.alertService.setAlert(err.message, 'blue');
                        },
                    });
                } else {
                    resolve(false);
                }
            }).catch(() => {
                resolve(false);
            });
        });
    }

    stepNameGenerator(step): { stepName: string, isClear: boolean } {
        let stepName: string;
        let isClear: boolean;
        if (step.name) {
            stepName = step.name;
            isClear = false;
            return {stepName, isClear};
        } else {
            if (this.stepsElement[step.target_key]) {
                if (this.stepsElement[step.target_key].errorReq) {
                    stepName = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.ERROR');
                    isClear = true;
                    return {stepName, isClear};
                } else {
                    stepName = this.helper.defaultContractStepName(step, this.stepsElement[step.target_key]);
                    isClear = true;
                    return {stepName, isClear};
                }
            } else {
                stepName = step.target_key ? this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.GET_INFO')
                    : this.helper.defaultContractStepName(step);
                isClear = true;
                return {stepName, isClear};
            }
        }
    }

    sortPredicate(index: number, item: CdkDrag<ContractStepModel>): boolean {
        let minIndex = 0;

        const steps = item.dropContainer.getSortedItems();
        for (const element of steps) {
            if (element.data.completed_at) {
                minIndex += 1;
            }
        }

        switch (item.data.target_type) {
            case 'general_credit_letter':
                const nullPayment = item.dropContainer.getSortedItems()
                    .filter((x: CdkDrag) => {
                        if (x.data.target_type === 'payment' && !x.data.target.payer_project) {
                            return x;
                        } else {
                            if (x.data.target_type === 'multi_payment') {
                                for (const key of Object.keys(this.stepsElement)) {
                                    if (this.stepsElement[key].paymentsData && this.stepsElement[key].paymentsData.filter(paymentData =>
                                        !paymentData.payment.payer_guid)[0]) {
                                        return x;
                                    }
                                }
                            }
                        }
                    });
                return !((index >= nullPayment[0].data.sort_index) || (index < minIndex));

            case 'credit_letter' :
                const payment = item.dropContainer.getSortedItems().filter((x: CdkDrag) => {
                  
                    if (x.data.target_type === 'payment' &&
                        this.stepsElement[x.data.target_key].payer_guid === (item.data.target.payer_project?.project?.guid || this.stepsElement[x.data.target_key]?.owner_guid) 
                    ) {
                        return x;
                    }
                    if (x.data.target_type === 'multi_payment') {
                        for (const key of Object.keys(this.stepsElement)) {
                            if (this.stepsElement[key].paymentsData && this.stepsElement[key].paymentsData
                              .filter(paymentData =>{
                                  return paymentData.payment.payer_guid === (item.data.target.payer_project?.project?.guid || this.stepsElement[item.data.target.target_key]?.owner_guid)
                              })[0]) {
                                return x;
                            }
                        }
                    }
                });
                return !((index >= payment[0]?.data.sort_index) || (index < minIndex));

            case 'payment':
                const creditLetter = item.dropContainer.getSortedItems().filter((x: CdkDrag) => {
                    if (item.data.target.master_project) {
                        if (x.data.target_type === 'credit_letter' &&
                            ((x.data.target?.payer_project?.project.guid || this.stepsElement[x.data.target_key]?.owner_guid) === this.stepsElement[item.data.target_key].payer_guid)) {
                            return x;
                        }
                    } else {
                        if (x.data.target_type === 'general_credit_letter') {
                            return x;
                        }
                    }
                });
                return !(creditLetter[0] && index <= creditLetter[0].data.sort_index);

            case 'multi_payment':
                let maxCreditLetterIndex = 0;
                const allCreditLetters = item.dropContainer.getSortedItems()
                    .filter((x: CdkDrag) => x.data.target_type === 'credit_letter' || x.data.target_type === 'general_credit_letter');
                if (this.stepsElement[item.data.target_key].paymentsData.length) {
                    for (const multiPayment of this.stepsElement[item.data.target_key].paymentsData) {
                        if (multiPayment.payment.payer_guid) {
                            const credit = allCreditLetters.filter((x: CdkDrag) => 
                              (x.data.target?.payer_project?.project.guid || this.stepsElement[x.data.target_key]?.owner_guid) === multiPayment.payment.payer_guid)[0];
                            if (maxCreditLetterIndex < credit.data.sort_index) {
                                maxCreditLetterIndex = credit.data.sort_index;
                            }
                        } else {
                            const credit = allCreditLetters.filter((x: CdkDrag) =>
                                x.data.target_type === 'general_credit_letter')[0];
                            if (maxCreditLetterIndex < credit.data.sort_index) {
                                maxCreditLetterIndex = credit.data.sort_index;
                            }
                        }
                    }

                    return index > maxCreditLetterIndex;
                } else {
                    return index >= minIndex;
                }

            default:
                return index >= minIndex;
        }
    }

    shareStepNameData(step: ContractStepModel) {
        let newStepName = this.stepNameGenerator(step)
        this.subStepDataChanged.next({
            stepName: newStepName.stepName,
            step: step
        });
    }

    onLoadElement(step: ContractStepModel): void {
        switch (step.target_type) {
            case 'file':
                this.documentApi.getFileData(step.target_key).subscribe({
                    next: res => {
                        if (this.stepsElement[step.target_key] &&
                            (JSON.stringify(this.stepsElement[step.target_key]) === JSON.stringify(res.document))) {
                            return;
                        }
                        this.stepsElement[step.target_key] = res.document;
                        this.stepsElement[step.target_key].errorReq = false;
                        this.shareStepNameData(step)
                    }, error: () => {
                        this.stepsElement[step.target_key] = {errorReq: true};
                        this.shareStepNameData(step)
                    },
                });
                break;

            case 'task':
                this.crmApi.getTask(step.target_key).subscribe({
                    next: res => {
                        if (this.stepsElement[step.target_key] &&
                            (JSON.stringify(this.stepsElement[step.target_key]) === JSON.stringify(res.task))) {
                            return;
                        }
                        this.stepsElement[step.target_key] = res.task;
                        this.stepsElement[step.target_key].errorReq = false;
                        this.shareStepNameData(step)
                    }, error: () => {
                        this.stepsElement[step.target_key] = {errorReq: true};
                        this.shareStepNameData(step)
                    },
                });
                break;

            case 'payment':
            case 'direct_payment':
                this.financeApi.getPayment(step.target_key).subscribe({
                    next: res => {
                        if (this.stepsElement[step.target_key] &&
                            (JSON.stringify(this.stepsElement[step.target_key]) === JSON.stringify(res.transaction))) {
                            return;
                        }
                        this.stepsElement[step.target_key] = res.transaction;
                        this.stepsElement[step.target_key].errorReq = false;
                        step.payment_data = {
                            payer_guid: res.transaction.payer_guid,
                            recipient_guid: res.transaction.recipient_guid,
                            recipient_type: res.transaction.recipient_type,
                            payer_type: res.transaction.payer_type,
                            total: Number(res.transaction.total),
                        };
                        this.shareStepNameData(step);
                    }, error: () => {
                        this.stepsElement[step.target_key] = {errorReq: true};
                        step.payment_data = {
                            payer_guid: null,
                            recipient_guid: null,
                            total: 0,
                        };
                        this.shareStepNameData(step);
                    },
                });
                break;

            case 'sub_contract':
                this.contractApi.getByGuid(step.target_key).subscribe({
                    next: res => {
                        if (this.stepsElement[step.target_key] &&
                            (JSON.stringify(this.stepsElement[step.target_key]) === JSON.stringify(res.contract))) {
                            return;
                        }
                        this.stepsElement[step.target_key] = res.contract;
                        this.stepsElement[step.target_key].errorReq = false;
                        this.shareStepNameData(step);
                    }, error: () => {
                        this.stepsElement[step.target_key] = {errorReq: true};
                        this.shareStepNameData(step);
                    },
                });
                break;

            case 'credit_letter':
            case 'general_credit_letter':
                this.getLetters(step);
                break;
            case 'multi_payment':
                this.financeApi.getMultiPayment(step.target_key).subscribe({
                    next: res => {
                        if (this.stepsElement[step.target_key] &&
                            (JSON.stringify(this.stepsElement[step.target_key]) === JSON.stringify(res))) {
                            return;
                        }
                        const state = {
                            paymentsData: [],
                            signedPayment: res.multi_payment.signed_payment_guid,
                            isMemberSign: false,
                            isSignPaymentsALot: false,
                        };
                        step.multi_payment_data = [];

                        res.payments_of_multi_payment.map(payment => {
                            const currentMemberInPayment = res.members_of_multi_payment.filter(member =>
                                member.signed_payment_guid === payment.guid &&
                                member.contract_member.member.guid === this.currentMember.guid)[0];

                            if (!state.isMemberSign) {
                                state.isMemberSign = !!currentMemberInPayment;
                            }
                            const paymentOfState = {
                                payment,
                                members: res.members_of_multi_payment.filter(member => member.signed_payment_guid === payment.guid),
                                isCurrentMemberSign: !!currentMemberInPayment,
                            };
                            state.paymentsData.push(paymentOfState);

                            const multiPaymentData = {
                                total: Number(payment.total),
                                recipient_guid: payment.recipient_guid,
                                payer_guid: payment.payer_guid,
                                payment_guid: payment.guid,
                                recipient_type: payment.recipient_type,
                                payer_type: payment.payer_type,
                            };
                            step.multi_payment_data.push(multiPaymentData);
                        });

                        state.isSignPaymentsALot = state.paymentsData.filter(payment => payment.members.length).length > 1;

                        this.stepsElement[step.target_key] = state;
                        this.stepsElement[step.target_key].errorReq = false;
                        this.shareStepNameData(step);
                    }, error: () => {
                        this.stepsElement[step.target_key] = {errorReq: true};
                        this.shareStepNameData(step);
                    },
                });
                break;
              }
             
    }

    getLetters(step): void {
        this.financeApi.getCreditLetter(step.target_key).subscribe({
            next: res => {
                if (this.stepsElement[step.target_key] &&
                    (JSON.stringify(this.stepsElement[step.target_key]) === JSON.stringify(res.credit_letter))) {
                    return;
                }
                this.stepsElement[step.target_key] = res.credit_letter;
                this.stepsElement[step.target_key].errorReq = false;
                this.shareStepNameData(step);
            }, error: () => {
                this.stepsElement[step.target_key] = {errorReq: true};
                this.shareStepNameData(step);
            },
        });
    }

    onSaveStep(roadmap: ContractStepModel[], modal: NgbModalRef): void {
        let isVerified = false;
        let targetKey = null;
        let targetName = null;
        let message = '';
        switch (this.target) {
            case 'file':
                targetKey = this.targetItem.key;
                targetName = this.targetItem.name;
                isVerified = this.targetItem.signed;
                message = this.translate.instant('ALERTS.FILE_ADDED');
                break;
            case 'task':
                targetKey = this.targetItem.task_alias;
                targetName = this.targetItem.title;
                isVerified = this.targetItem.status_current.is_successful;
                message = this.translate.instant('ALERTS.TASK_ADDED');
                break;
            case 'sub_contract':
                targetKey = this.targetItem.guid;
                targetName = this.targetItem.name;
                isVerified = !!this.targetItem.comleted_at;
                message = this.translate.instant('ALERTS.CONTRACT_ADDED');
                break;
        }
        if (roadmap.filter(step => step.target_key === targetKey).length) {
            return this.alertService.setAlert(message, 'blue');
        }

        this.selectedStep.target_key = targetKey;
        this.selectedStep.targetName = targetName;
        this.selectedStep.target = {};
        this.selectedStep.target.is_verified = isVerified;

        this.target = '';
        this.emitOnEdit.emit(true);

        this.onLoadElement(this.selectedStep);
        modal.close();
    }

    onStepTarget(item: ContractStepModel, content: TemplateRef<any>): void {
        this.selectedStep = item;
        if (!this.selectedStep.target) {
            this.selectedStep.target = null;
        }
        if (item.target_type === 'payment' || item.target_type === 'direct_payment') {
            if (this.stepsElement[item.target_key]) {
                this.paymentModel = this.stepsElement[item.target_key];
            } else {
                this.paymentModel = new PaymentTransactionModel();
            }
        }

        if (item.target_type === 'multi_payment') {
            this.paymentModel = new PaymentTransactionModel();
        }
        this.target = item.target_type;
        this.modalService.openTemplateModal(content, 'xl').result.then(_ => {
            this.target = '';

            this.subStepDataChanged.next({
                stepName: this.stepNameGenerator(item).stepName,
                step: item
            })
        });
    }

    onAddPayment(isMulti, modal: NgbModalRef): void {
        const data: TPaymentData = {
            payer_guid: this.paymentModel.payer_guid,
            recipient_guid: this.paymentModel.recipient_guid,
            recipient_type: this.paymentModel.recipient_type,
            payer_type: this.paymentModel.payer_type,
            total: this.paymentModel.total ? Number(this.paymentModel.total) : 0,
        };
        this.emitOnEdit.emit(true);
        modal.close();
        this.target = '';

        if (isMulti) {
            if (!this.selectedStep.multi_payment_data) {
                this.selectedStep.multi_payment_data = [];
            }
            const multiPayment = {
                payment: data,
                members: [],
                isCurrentMemberSign: false,
            };

            if (this.selectedStep.target_key && this.stepsElement[this.selectedStep.target_key]) {
                this.stepsElement[this.selectedStep.target_key].paymentsData.push(multiPayment);
            }
            this.selectedStep.multi_payment_data.push(data);
        } else {
            this.selectedStep.payment_data = data;
        }
    }

    watchTarget(item, content: TemplateRef<any>): void {
        this.selectedStep = item;
        this.typeOpenViewNodal = item.target_type;
        this.modalService.openTemplateModal(content, 'xl').result.then(_ => {
            this.typeOpenViewNodal = '';
        });
    }

    onEditStepField(field: string, item, $event): void {
        item[field] = $event;
        this.emitOnEdit.emit(true);
    }

    /**
     * Подписание / Отзыв подписи обеспечения
     * @param sign true - подписание; false - отзыв подписи
     */
    onSignOnOffCreditLetter(pin: string, creditLetterGuid: string, sign: boolean, contractGuid: string): Observable<any> {
        return this.financeApi.signOnOffCreditLetter(creditLetterGuid, pin, sign, contractGuid)
            .pipe(catchError(e => {
                this.alertService.setAlert(e.message, 'blue')
                return EMPTY
            }))
    }

    onSignPayment(pin, signingPayment): void {
        this.financeApi.signPayment(this.selectedStep?.target_key || '', signingPayment.payment.guid, pin).subscribe({
            next: res => {
                this.alertService.setAlert(res.message, 'green');
                this.onLoadElement(this.selectedStep);
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            },
        });
    }

    onTerminatePayment(pin, signingPayment): void {
        this.financeApi.terminatePayment(this.selectedStep?.target_key || '', signingPayment.payment.guid, pin).subscribe({
            next: res => {
                this.alertService.setAlert(res.message, 'green');
                this.onLoadElement(this.selectedStep);
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            },
        });
    }

    deleteMultiPaymentItem(payment, step): void {
        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_PAYMENT'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                step.multi_payment_data = step.multi_payment_data.filter(x => {
                    if (x.payment_guid) {
                        return x.payment_guid !== payment.payment.guid;
                    } else {
                        return JSON.stringify(x) !== JSON.stringify(payment.payment);
                    }
                });

                if (step.target_key) {
                    this.stepsElement[step.target_key].paymentsData = this.stepsElement[step.target_key].paymentsData.filter(
                        paymentData => paymentData !== payment);
                }

                this.emitOnEdit.emit(true);
            }
        }).catch(() => {
            return;
        });
    }

    savePayment(modal, payment): void {
        if (this.selectedStep.multi_payment_data) {
            let multi_payment_data = this.selectedStep.multi_payment_data.filter(multiPayment => multiPayment.payment_guid === payment.guid)[0]
            this.setPaymentData(multi_payment_data, payment)
        }
        if (this.selectedStep.target_key) {
            let payment_data = this.stepsElement[this.selectedStep.target_key].paymentsData.filter(payment => payment.payment.guid === payment.guid)[0]?.payment;
            payment_data ? this.setPaymentData(payment_data, payment) : null;
        }
        modal.close();
        this.emitOnEdit.emit(true);
    }

    setPaymentData(data: TPaymentData, payment: TPaymentData) {
        data.total = payment.total
        data.recipient_guid = payment.recipient_guid
        data.recipient_type = payment.recipient_type
        data.payer_guid = payment.payer_guid
        data.payer_type = payment.payer_type
    }

    onSignTask(targetKey: string,
               targetType: string,
               data: {
                   contract_guid: string,
                   PIN_code: string
               }
    ): void {
        this.contractApi.signTask(targetKey, targetType, data).subscribe({
            next: res => {
                this.alertService.setAlert(res.message, 'green');
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    onTerminateTask(targetKey: string,
                    targetType: string,
                    data: {
                        contract_guid: string,
                        PIN_code: string
                    }
    ): void {
        this.contractApi.terminateTask(targetKey, targetType, data).subscribe({
            next: res => {
                this.alertService.setAlert(res.message, 'green');
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }
}
